import React, { useEffect, useState } from "react"
import type { ElementType } from "react"

import { InstanceFeatureFlagType } from "@/services/HardCoded"
import ApiService from "@/services/Api"
import AccountOwnerContactUpdateService from "@/services/AccountOwnerContactUpdate"
import useValidation, { ErrorType } from "@/hooks/useValidation"

import { Star as StarIcon } from "@material-ui/icons"

import LetalkCommunityBanner from "@/assets/images/banners/image-popup-community-letalk.png"
import { SurveyFormContent } from "@/components/SurveyFormContent"
import { useSurveySubmit } from "@/contexts/SurveySubmitContext"
import { MultiQuestionSurvey, MultiQuestionSurveyValue } from "@/popups/templates/MultiQuestionSurvey"
import { CheckboxSurvey, CheckboxSurveyValue } from "@/popups/templates/CheckboxSurvey"
import { AccountOwnerContactUpdate, AccountOwnerContactUpdateValue } from "@/popups/templates/AccountOwnerContactUpdate"
import n8nSurveyEndpoint from "@/config/popup"

export type Popup = {
	type: string
	image?: string
	chipIcon?: ElementType
	chipText?: string
	chipClassName?: string
	title?: string
	body?: string
	confirmButtonText?: string
	onAction?: () => void
	content?: ElementType
	shouldHideDoNotShowAgain?: boolean
}

export type PopupType =
	| "welcome-popup"
	| "letalk-community"
	| "survey-1"
	| "survey-2"
	| "survey-3"
	| "template-multi-survey"
	| "template-checkbox-survey"
	| "account-owner-contact-update"

const Survey1Form = () => (
	<SurveyFormContent
		options={[
			{ value: "excelente", label: "Excelente" },
			{ value: "bom", label: "Bom" },
			{ value: "regular", label: "Regular" },
			{ value: "ruim", label: "Ruim" }
		]}
		responseMode="single"
		endpoint={n8nSurveyEndpoint}
	/>
)

const Survey2Form = () => (
	<SurveyFormContent
		options={[
			{ value: "facilidade", label: "Facilidade de uso" },
			{ value: "velocidade", label: "Velocidade da plataforma" },
			{ value: "integracao", label: "Integrações disponíveis" },
			{ value: "suporte", label: "Qualidade do suporte" }
		]}
		responseMode="multi"
		allowOther
		endpoint={n8nSurveyEndpoint}
	/>
)

const Survey3Form = () => (
	<SurveyFormContent
		options={[
			{ value: "atendimento", label: "Atendimento ao cliente" },
			{ value: "automacao", label: "Automação de mensagens" },
			{ value: "relatorios", label: "Relatórios e métricas" }
		]}
		responseMode="single"
		allowOther
		endpoint={n8nSurveyEndpoint}
	/>
)

const MultiQuestionSurveyForm = () => {
	const { submitFnRef, onClosePermanently } = useSurveySubmit()
	const [formValue, setFormValue] = useState<MultiQuestionSurveyValue>({ q1: [], q2: "", q3: "" })

	submitFnRef.current = async () => {
		try {
			await ApiService.post("/webhooks/external/call", { data: formValue, url: n8nSurveyEndpoint })
		} finally {
			onClosePermanently()
		}
	}

	return <MultiQuestionSurvey value={formValue} onChange={setFormValue} />
}

const CheckboxSurveyForm = () => {
	const { submitFnRef, onClosePermanently } = useSurveySubmit()
	const [formValue, setFormValue] = useState<CheckboxSurveyValue>({ selected: [], otherText: "" })

	submitFnRef.current = async () => {
		try {
			await ApiService.post("/webhooks/external/call", { data: formValue, url: n8nSurveyEndpoint })
		} finally {
			onClosePermanently()
		}
	}

	return <CheckboxSurvey value={formValue} onChange={setFormValue} />
}

const AccountOwnerContactUpdateForm = () => {
	const { submitFnRef, onClosePermanently, setIsConfirmDisabled } = useSurveySubmit()
	const { triggerValidation, validation } = useValidation()
	const [formValue, setFormValue] = useState<AccountOwnerContactUpdateValue>({ name: "", phone: "" })

	const isFormInvalid = formValue.name.trim() === "" || formValue.phone.trim() === ""

	useEffect(() => {
		setIsConfirmDisabled?.(isFormInvalid)
	}, [isFormInvalid, setIsConfirmDisabled])

	// The parent Popups component doesn't know each popup form's submit logic.
	// Each form registers its own submit function in the shared ref (via useSurveySubmit)
	// so the parent can trigger it on confirm without being coupled to the specific form.
	// This is the same pattern already used by SurveyFormContent.
	submitFnRef.current = async () => {
		try {
			await AccountOwnerContactUpdateService.submit(formValue)
			onClosePermanently()
		} catch (error) {
			triggerValidation(error as ErrorType)
		}
	}

	return (
		<AccountOwnerContactUpdate
			value={formValue}
			onChange={setFormValue}
			errors={{ name: validation.name, phone: validation.phone }}
		/>
	)
}

class PopupUtil {
	private readonly popupTypeToPopupData: Record<PopupType, Omit<Popup, "type">> = {
		"welcome-popup": {
			title: "Continue otimizando o seu atendimento com IA",
			body: "A sua equipe atingiu o limite de créditos de IA. Continue otimizando respostas, utilizando os blocos inteligentes no bot, resumindo atendimentos e ajustando o tom das mensagens. Mantenha o time produtivo e as conversas cada vez mais eficazes com a força da inteligência artificial.",
			confirmButtonText: "FALAR COM ESPECIALISTA",
			chipText: "Novidade",
			image: "https://letalk.com.br/wp-content/uploads/2025/01/Blog-Banner-API-Waba.jpg",
			chipIcon: StarIcon,
			onAction: () => {
				window.open("https://letalk.com.br/", "_blank")
			}
		},
		"letalk-community": {
			title: "Você já faz parte da Comunidade Letalk?",
			body: "Tenha acesso exclusivo a avisos de novas funcionalidades, estratégias e conteúdos práticos para melhorar seus resultados.",
			confirmButtonText: "Fazer parte da Comunidade",
			chipText: "Novidade",
			image: LetalkCommunityBanner,
			chipIcon: StarIcon,
			onAction: () => {
				window.open("https://ia.letalk.com.br/comunidade/", "_blank")
			}
		},
		"survey-1": {
			image: "https://placehold.co/600x360",
			title: "Como você avalia nossa nova funcionalidade?",
			confirmButtonText: "Enviar",
			content: Survey1Form
		},
		"survey-2": {
			image: "https://placehold.co/600x360",
			title: "O que mais te agrada na Letalk?",
			confirmButtonText: "Enviar",
			content: Survey2Form
		},
		"survey-3": {
			title: "Qual funcionalidade você mais utiliza?",
			confirmButtonText: "Enviar",
			content: Survey3Form
		},
		"template-multi-survey": {
			title: "Pesquisa rápida",
			confirmButtonText: "Enviar",
			content: MultiQuestionSurveyForm
		},
		"template-checkbox-survey": {
			title: "O que mais te agrada na Letalk?",
			confirmButtonText: "Enviar",
			content: CheckboxSurveyForm
		},
		"account-owner-contact-update": {
			title: "Atualize os seus dados de contato Letalk!",
			body: "Mantenha seus dados de contato atualizados para que nossa equipe consiga falar com você sempre que precisar.",
			confirmButtonText: "Enviar",
			content: AccountOwnerContactUpdateForm,
			shouldHideDoNotShowAgain: true
		}
	}

	private readonly popupTypeToFeatureFlag: Record<PopupType, InstanceFeatureFlagType> = {
		"welcome-popup": "canShowWelcomePopup",
		"letalk-community": "canShowWelcomePopup",
		"survey-1": "canShowWelcomePopup",
		"survey-2": "canShowWelcomePopup",
		"survey-3": "canShowWelcomePopup",
		"template-multi-survey": "canShowWelcomePopup",
		"template-checkbox-survey": "canShowWelcomePopup",
		"account-owner-contact-update": "canShowWelcomePopup"
	}

	mountPopups (): Popup[] {
		const popups: Popup[] = []

		const popupTypeToIsEnabled: Record<PopupType, boolean> = {
			"welcome-popup": false, // HardCoded.checkFeatureFlag(this.popupTypeToFeatureFlag["welcome-popup"])
			"letalk-community": false,
			"survey-1": false,
			"survey-2": false,
			"survey-3": false,
			"template-multi-survey": false,
			"template-checkbox-survey": false,
			"account-owner-contact-update": true
		}

		for (const key in popupTypeToIsEnabled) {
			const popupType = key as PopupType

			if (popupTypeToIsEnabled[popupType]) {
				popups.push({
					type: popupType,
					...this.popupTypeToPopupData[popupType]
				})
			}
		}

		return popups
	}
}

export default new PopupUtil()
